"use client";

import { createContext, useCallback, useContext, useRef } from "react";

/**
 * SignDocContext — coordinates the "D" hotkey re-open with scene-local
 * sign-document modals.
 *
 *   Each SignDocumentModal calls `register(id, reopen)` on mount. The most
 *   recent registration wins — pressing D re-opens whichever modal was
 *   most recently set up by the current scene. If no modal has registered
 *   (e.g. the scene has no sign doc), D is a no-op.
 */

type ReopenFn = () => void;

type Ctx = {
  register: (id: string, reopen: ReopenFn) => () => void;
  reopenActive: () => void;
};

const SignDocCtx = createContext<Ctx | null>(null);

export function SignDocProvider({ children }: { children: React.ReactNode }) {
  const stackRef = useRef<{ id: string; reopen: ReopenFn }[]>([]);

  const register = useCallback((id: string, reopen: ReopenFn) => {
    const entry = { id, reopen };
    stackRef.current.push(entry);
    return () => {
      stackRef.current = stackRef.current.filter((e) => e !== entry);
    };
  }, []);

  const reopenActive = useCallback(() => {
    const top = stackRef.current[stackRef.current.length - 1];
    top?.reopen();
  }, []);

  return (
    <SignDocCtx.Provider value={{ register, reopenActive }}>
      {children}
    </SignDocCtx.Provider>
  );
}

export function useSignDoc(): Ctx {
  const ctx = useContext(SignDocCtx);
  if (!ctx) {
    // Fallback no-op so components can render outside the provider in tests.
    return { register: () => () => {}, reopenActive: () => {} };
  }
  return ctx;
}
